import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '@theraply/api-client';

interface DocumentExpiration {
  documentId: string;
  documentType: string;
  expirationDate: string;
  daysUntilExpiration: number;
  urgencyLevel: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
}


export default function ExpirationWarning() {
  const [expirations, setExpirations] = useState<DocumentExpiration[]>([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadExpirations();
  }, []);

  const loadExpirations = async () => {
    try {
      // This endpoint would need to be added to return expirations for the current therapist
      const response = await apiClient.get('/therapist/documents/expirations');
      setExpirations(response.data.expirations || []);
    } catch (err) {
      console.error('Failed to load expirations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = (documentId: string) => {
    setDismissed(prev => new Set(prev).add(documentId));
    // Optionally persist dismissal to localStorage
    const dismissedIds = Array.from(dismissed);
    dismissedIds.push(documentId);
    localStorage.setItem('dismissed-expirations', JSON.stringify(dismissedIds));
  };

  // Filter out dismissed and non-urgent expirations
  const activeExpirations = expirations.filter(
    exp => !dismissed.has(exp.documentId) && exp.daysUntilExpiration <= 60
  );

  if (loading || activeExpirations.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {activeExpirations.map((exp) => {
        const isUrgent = exp.daysUntilExpiration <= 7;
        const isWarning = exp.daysUntilExpiration <= 30;

        const bgColor = isUrgent
          ? 'bg-red-50 border-red-300'
          : isWarning
          ? 'bg-orange-50 border-orange-300'
          : 'bg-yellow-50 border-yellow-300';

        const iconColor = isUrgent
          ? 'text-red-600'
          : isWarning
          ? 'text-orange-600'
          : 'text-yellow-600';

        const textColor = isUrgent
          ? 'text-red-900'
          : isWarning
          ? 'text-orange-900'
          : 'text-yellow-900';

        return (
          <div
            key={exp.documentId}
            className={`${bgColor} border-l-4 rounded-lg p-4 relative`}
          >
            <button
              onClick={() => handleDismiss(exp.documentId)}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
              aria-label="Dismiss"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="flex items-start">
              <div className={`flex-shrink-0 ${iconColor}`}>
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>

              <div className="ml-3 flex-1 pr-8">
                <h3 className={`text-sm font-medium ${textColor}`}>
                  {isUrgent ? 'URGENT: ' : ''}Document Expiring Soon
                </h3>
                <div className={`mt-2 text-sm ${textColor}`}>
                  <p>
                    Your <strong>{formatDocumentType(exp.documentType)}</strong> will
                    expire in{' '}
                    <strong className="font-bold">
                      {exp.daysUntilExpiration} {exp.daysUntilExpiration === 1 ? 'day' : 'days'}
                    </strong>{' '}
                    on {formatDate(exp.expirationDate)}.
                  </p>
                  {isUrgent && (
                    <p className="mt-2 font-semibold">
                      Please upload your renewed document immediately to avoid account
                      suspension.
                    </p>
                  )}
                </div>

                <div className="mt-4">
                  <Link
                    to="/documents"
                    className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md ${
                      isUrgent
                        ? 'text-white bg-red-600 hover:bg-red-700'
                        : isWarning
                        ? 'text-white bg-orange-600 hover:bg-orange-700'
                        : 'text-yellow-900 bg-yellow-200 hover:bg-yellow-300'
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500`}
                  >
                    Upload Renewed Document
                    <svg
                      className="ml-2 -mr-1 h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function formatDocumentType(type: string): string {
  return type
    .split('_')
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
